import { useEffect, useMemo, useState } from "react";
import { getError } from "~/lib/handle-error";
import { useCanUpdateCase } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/UpdateCase";
import {
  GetAggregateScoresQuery,
  GetCaseDetailsDocument,
  useCloseCaseMutation,
  useGetAggregateScoresLazyQuery,
  useGetCaseDetailsQuery,
} from "~/operations";
import { useSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import { ObservableQuery } from "@apollo/client";
import { CaseTicket } from "~/pages/tickets/types";
import { AssetContextualLinkType } from "~/pages/space/vulnerabilities/components/exposed-assets";
import { SpaceOrWorkspaceScope, SpaceScope } from "~/hooks/useScope";
import { AssetsProps } from "~/pages/tickets/ticket/TicketPage/TicketAssetsSection/types";

type UseCaseDetailsPageParams = {
  space: SpaceScope;
  scope: SpaceOrWorkspaceScope;
};

export function useTicketDetailsPage({
  space,
  scope,
}: UseCaseDetailsPageParams) {
  const [searchParams] = useSearchParams();
  const caseMrn = searchParams.get("caseMrn") || "";
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: caseData,
    loading: caseLoading,
    error: caseError,
    fetchMore,
  } = useGetCaseDetailsQuery({
    variables: {
      mrn: caseMrn,
      mitigatedAssetsFirst: 10,
      affectedAssetsFirst: 10,
    },
    skip: !caseMrn,
  });

  const hasCloseCasePermissions = useCanUpdateCase(space);

  const [closeCase, { loading: closeCaseLoading }] = useCloseCaseMutation({
    refetchQueries: [GetCaseDetailsDocument],
  });

  const caseDetails =
    caseData?.case?.__typename === "Case" ? caseData?.case : undefined;

  const findingRefs = [
    ...(caseDetails?.queryRefs || []),
    ...(caseDetails?.vulnerabilityRefs || []),
  ];

  const findingMrns = findingRefs
    .map((ref) => ref?.findingMrn)
    .filter((mrn): mrn is string => Boolean(mrn));

  // Stabilize the findingMrns key to avoid unnecessary re-renders
  const findingMrnsKey = useMemo(
    () => findingMrns.join(","),
    [findingMrns.length, caseDetails],
  );

  const [containedFindings, setContainedFindings] = useState<
    NonNullable<
      Extract<
        GetAggregateScoresQuery["aggregateScores"],
        { __typename: "AggregateScoresConnection" }
      >["edges"]
    >
  >([]);
  const [aggScoreLoading, setAggScoreLoading] = useState(false);

  const [fetchAggregateScores] = useGetAggregateScoresLazyQuery();

  // Fetch aggregate scores for each finding MRN in parallel
  useEffect(() => {
    if (!caseMrn || findingMrns.length === 0) {
      setContainedFindings([]);
      return;
    }

    const fetchFindings = async () => {
      setAggScoreLoading(true);
      try {
        const results = await Promise.allSettled(
          findingMrns.map((findingMrn) =>
            fetchAggregateScores({
              variables: {
                entityMrn: scope.mrn,
                filter: { findingMrn },
              },
            }),
          ),
        );

        const failedCount = results.filter(
          (r) => r.status === "rejected",
        ).length;
        if (failedCount > 0) {
          enqueueSnackbar(
            `Failed to load ${failedCount} finding${failedCount > 1 ? "s" : ""}`,
            { variant: "warning" },
          );
        }

        const findings = results.flatMap((result) => {
          if (result.status === "rejected") {
            return [];
          }
          const scores = result.value.data?.aggregateScores;
          if (scores?.__typename === "AggregateScoresConnection") {
            return scores.edges || [];
          }
          return [];
        });

        // Filter to unique findings by findingMrn
        const uniqueFindings = findings.filter(
          (finding, index, self) =>
            index ===
            self.findIndex(
              (f) => f?.node?.findingMrn === finding?.node?.findingMrn,
            ),
        );

        setContainedFindings(uniqueFindings);
      } finally {
        setAggScoreLoading(false);
      }
    };

    fetchFindings();
  }, [
    caseMrn,
    findingMrnsKey,
    scope.mrn,
    fetchAggregateScores,
    enqueueSnackbar,
  ]);

  const fetchMoreAffectedAssets: ObservableQuery["fetchMore"] = (params) => {
    const { variables } = params;
    return fetchMore({
      variables: {
        affectedAssetsFirst: variables?.first,
        affectedAssetsAfter: variables?.after,
      },
    });
  };

  const fetchMoreMitigatedAssets: ObservableQuery["fetchMore"] = (params) => {
    const { variables } = params;
    return fetchMore({
      variables: {
        mitigatedAssetsFirst: variables?.first,
        mitigatedAssetsAfter: variables?.after,
      },
    });
  };

  const handleCloseCase = async () => {
    if (!hasCloseCasePermissions) return;

    try {
      await closeCase({
        variables: {
          input: {
            mrn: caseMrn,
          },
        },
      });
    } catch (e) {
      const errorMessage = getError(e);
      enqueueSnackbar(
        `Something went wrong while attempting to close the case: ${errorMessage}`,
        { variant: "error" },
      );
    }
  };

  const tickets = (caseDetails?.ticketRefs || []).map<CaseTicket>(
    (ticketRef) => ({
      id: ticketRef.ticketId || "",
      href: ticketRef.url || "",
      type: ticketRef.type,
    }),
  );

  const affectedAssetsConnection =
    caseDetails?.affectedAssets?.__typename === "CaseAssetsConnection"
      ? caseDetails?.affectedAssets
      : undefined;

  const mitigatedAssetsConnection =
    caseDetails?.mitigatedAssets?.__typename === "CaseAssetsConnection"
      ? caseDetails?.mitigatedAssets
      : undefined;

  const affectedAssets =
    (affectedAssetsConnection?.edges || []).flatMap((e) => e.node ?? []) || [];
  const affectedAssetsTotalCount = affectedAssetsConnection?.totalCount || 0;

  const mitigatedAssets =
    (mitigatedAssetsConnection?.edges || []).flatMap((e) => e.node ?? []) || [];
  const mitigatedAssetsTotalCount = mitigatedAssetsConnection?.totalCount || 0;

  // For multi-finding tickets, link assets to the asset overview page
  // since they may be related to different findings
  const assetsCommonProps = {
    scope,
    contextId: "",
    urlContextType: AssetContextualLinkType.Asset,
  };

  const affectedAssetsProps: AssetsProps = {
    ...assetsCommonProps,
    assets: affectedAssets,
    totalCount: affectedAssetsTotalCount,
    filteredTotalCount: affectedAssetsTotalCount,
    pageInfo: affectedAssetsConnection?.pageInfo,
    fetchMore: fetchMoreAffectedAssets,
    handleSortClick(_: string): void {},
    orderBy: { direction: "", field: "" },
  };

  const mitigatedAssetsProps: AssetsProps = {
    ...assetsCommonProps,
    assets: mitigatedAssets,
    totalCount: mitigatedAssetsTotalCount,
    filteredTotalCount: mitigatedAssetsTotalCount,
    pageInfo: mitigatedAssetsConnection?.pageInfo,
    fetchMore: fetchMoreMitigatedAssets,
    handleSortClick(_: string): void {},
    orderBy: { direction: "", field: "" },
  };

  return {
    caseDetails,
    totalRefsCount: caseDetails?.totalRefsCount || 0,
    closedRefsCount: caseDetails?.closedRefsCount || 0,
    isLoading: caseLoading || aggScoreLoading,
    isCloseCaseLoading: closeCaseLoading,
    handleCloseCase,
    findings: containedFindings,
    affectedAssetsProps,
    mitigatedAssetsProps,
    tickets,
    caseError,
  };
}
